import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router"
import { removeFromCart } from "../../../store/Cart"
import { message } from "../../../utils/message"
import { saveSuccessState, type CheckoutResult, type SuccessState } from "./cart-model"
import type { SupplierCartGroup } from "./types"
import { LIST_STORAGE_KEY } from "./utils"

/**
 * After a submit: take the suppliers whose order went through out of the cart
 * (the others stay, with their error shown by the caller) and open the success
 * page (B10). Returns false when nothing went through, so the caller stays put.
 */
export function useFinishCheckout() {
  const { t } = useTranslation("order")
  const dispatch = useDispatch()
  const navigate = useNavigate()

  return React.useCallback(
    (send: boolean, results: CheckoutResult[], groups: Record<string, SupplierCartGroup>): boolean => {
      const done = results.filter((r) => r.orderId)
      if (done.length === 0) return false

      const doneIds = new Set(done.map((r) => r.supplierId))
      const lines = Object.entries(groups)
        .filter(([id]) => doneIds.has(id))
        .flatMap(([, group]) => group.items)
      lines.forEach((item) => dispatch(removeFromCart({ productId: item.id, supplierBusinessId: item.supplierBusinessId })))

      // Catalogue scroll/filter memory, as the old checkout cleared it after an order.
      if (done.length === results.length) {
        try {
          const keys: string[] = []
          for (let i = 0; i < sessionStorage.length; i++) {
            const k = sessionStorage.key(i)
            if (k && k.startsWith(`${LIST_STORAGE_KEY}.`)) keys.push(k)
          }
          keys.forEach((k) => sessionStorage.removeItem(k))
        } catch {
          /* ignore */
        }
      }

      const state: SuccessState = { send, results, lines }
      saveSuccessState(state)
      message.success(send ? t("cart.success.toastSent", { count: done.length }) : t("cart.success.toastSaved", { count: done.length }))
      navigate("/buyer-sales/success", { state })
      return true
    },
    [dispatch, navigate, t],
  )
}
